import { Table, Form, Button } from 'react-bootstrap';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const categories = [
  "SalesCredit", "SalesPayment", "Consult", "Lab", "Dressing", "Delivery",
  "Observation", "Admission", "Other", "Electricity", "Expense", "Service", "Office Supplies"
];

const ExpenseCategory = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: expensesData } = useGetExpensesQuery({ startDate, endDate });

  const groupedByDate = {};
  expensesData?.expenses?.forEach(expense => {
    const expenseDate = new Date(expense.date).toISOString().split('T')[0];
    if (expenseDate >= startDate && expenseDate <= endDate) {
      if (!groupedByDate[expenseDate]) groupedByDate[expenseDate] = {};
      const category = expense.category;
      const amount = Number(expense.amount || 0);
      groupedByDate[expenseDate][category] = (groupedByDate[expenseDate][category] || 0) + amount;
    }
  });

  const columnTotals = {};
  Object.values(groupedByDate).forEach(dateEntry => {
    categories.forEach(category => {
      columnTotals[category] = (columnTotals[category] || 0) + (dateEntry[category] || 0);
    });
  });

  const totalExpenses = Object.values(columnTotals).reduce((sum, val) => sum + val, 0);

  // 🔄 Export to Excel
  const handleExport = () => {
    const rows = Object.entries(groupedByDate).map(([date, categoryData]) => {
      const row = { Date: date };
      let rowTotal = 0;
      categories.forEach(cat => {
        const value = categoryData[cat] || 0;
        row[cat] = value;
        rowTotal += value;
      });
      row['Total Expenses'] = rowTotal;
      return row;
    });

    // Add summary row
    const summaryRow = { Date: 'Total' };
    categories.forEach(cat => {
      summaryRow[cat] = columnTotals[cat] || 0;
    });
    summaryRow['Total Expenses'] = totalExpenses;
    rows.push(summaryRow);

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expense Report');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `Expense_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="container mt-4">
      <h1 className="text-center">Income And Expense Report</h1>

      <Form className="mb-3 d-flex justify-content-center">
        <Form.Group className="mx-2">
          <Form.Label>Start Date:</Form.Label>
          <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>End Date:</Form.Label>
          <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </Form.Group>
      </Form>

      {startDate && endDate && (
        <>
          <Table striped bordered hover responsive className="table-responsive">
            <thead className="text-center">
              <tr>
                <th>Date</th>
                {categories.map(category => (
                  <th key={category}>{category}</th>
                ))}
                <th>Total Expenses</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {Object.entries(groupedByDate).map(([date, categoryData]) => {
                const rowTotal = categories.reduce((sum, cat) => sum + (categoryData[cat] || 0), 0);
                return (
                  <tr key={date}>
                    <td>{date}</td>
                    {categories.map(cat => (
                      <td key={cat}>{categoryData[cat]?.toFixed(2) || '0.00'}</td>
                    ))}
                    <td>{rowTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
              <tr className="fw-bold">
                <td>Total</td>
                {categories.map(cat => (
                  <td key={cat}>{columnTotals[cat]?.toFixed(2) || '0.00'}</td>
                ))}
                <td>{totalExpenses.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>

          <div className="text-center mt-3">
            <Button variant="success" onClick={handleExport}>
              Export to Excel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExpenseCategory;